import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import type { Category } from "../types/category";
import { DEFAULT_CATEGORIES } from "../constants/categories";
import { supabase } from "../lib/supabase";

interface CategoriesContextType {
  categories: Category[];
  getCategoryById: (id: string) => Category | undefined;
  addCategory: (category: Omit<Category, "createdAt" | "updatedAt">) => Promise<boolean>;
  updateCategory: (id: string, updates: Partial<Category>) => Promise<boolean>;
  removeCategory: (id: string) => Promise<boolean>;
  reloadCategories: () => Promise<void>;
  exportCategories: () => string;
  importCategories: (jsonData: string) => Promise<boolean>;
  loading: boolean;
}

const CategoriesContext = createContext<CategoriesContextType | undefined>(undefined);

export const CategoriesProvider = ({ children }: { children: ReactNode }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  // ✅ Load categories from Supabase
  const loadCategories = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('order', { ascending: true });

      if (error) {
        console.error('❌ Kategoriler yüklenemedi:', error);
        setCategories(DEFAULT_CATEGORIES);
        return;
      }

      if (data && data.length > 0) {
        const mapped: Category[] = data.map(cat => ({
          id: cat.id,
          name: cat.name,
          slug: cat.slug,
          icon: cat.icon,
          seoTitle: cat.seo_title || '',
          seoDescription: cat.seo_description || '',
          order: cat.order,
          isFeatured: cat.is_featured || false,
          createdAt: new Date(cat.created_at).getTime(),
          updatedAt: new Date(cat.updated_at).getTime(),
        }));
        setCategories(mapped);
        console.log(`✅ ${mapped.length} kategori yüklendi`);
      } else {
        setCategories(DEFAULT_CATEGORIES);
      }
    } catch (error) {
      console.error('❌ Kategori yükleme hatası:', error);
      setCategories(DEFAULT_CATEGORIES);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const getCategoryById = (id: string) => {
    return categories.find(cat => cat.id === id || cat.slug === id);
  };

  // ✅ Add category to Supabase - ASYNC with proper error handling
  const addCategory = async (category: Omit<Category, "createdAt" | "updatedAt">): Promise<boolean> => {
    try {
      console.log('🔄 Kategori ekleniyor:', category.name);
      
      const { data, error } = await supabase
        .from('categories')
        .insert({
          id: category.id,
          name: category.name,
          slug: category.slug,
          icon: category.icon,
          seo_title: category.seoTitle,
          seo_description: category.seoDescription,
          order: category.order,
          is_featured: category.isFeatured,
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Kategori eklenemedi:', error);
        return false;
      }

      console.log('✅ Kategori eklendi:', data.id);
      await loadCategories();
      return true;
    } catch (error) {
      console.error('❌ Kategori ekleme hatası:', error);
      return false;
    }
  };

  // ✅ Update category in Supabase - ASYNC with proper error handling
  const updateCategory = async (id: string, updates: Partial<Category>): Promise<boolean> => {
    try {
      console.log('🔄 Kategori güncelleniyor:', id);
      
      const dbUpdates: any = {
        updated_at: new Date().toISOString(),
      };

      if (updates.name) dbUpdates.name = updates.name;
      if (updates.slug) dbUpdates.slug = updates.slug;
      if (updates.icon) dbUpdates.icon = updates.icon;
      if (updates.seoTitle !== undefined) dbUpdates.seo_title = updates.seoTitle;
      if (updates.seoDescription !== undefined) dbUpdates.seo_description = updates.seoDescription;
      if (updates.order !== undefined) dbUpdates.order = updates.order;
      if (updates.isFeatured !== undefined) dbUpdates.is_featured = updates.isFeatured;

      const { error } = await supabase
        .from('categories')
        .update(dbUpdates)
        .eq('id', id);

      if (error) {
        console.error('❌ Kategori güncellenemedi:', error);
        return false;
      }

      console.log('✅ Kategori güncellendi:', id);
      await loadCategories();
      return true;
    } catch (error) {
      console.error('❌ Kategori güncelleme hatası:', error);
      return false;
    }
  };

  // ✅ Remove category from Supabase - ASYNC with proper error handling
  const removeCategory = async (id: string): Promise<boolean> => {
    try {
      console.log('🔄 Kategori siliniyor:', id);
      
      const { data: listings, error: checkError } = await supabase
        .from('listings')
        .select('id')
        .eq('category_slug', id)
        .in('status', ['approved', 'pending'])
        .limit(1);

      if (checkError) {
        console.error('❌ Kategori kullanım kontrolü hatası:', checkError);
        return false;
      }

      if (listings && listings.length > 0) {
        console.warn('⚠️ Kategori kullanımda, silinemez:', id);
        return false;
      }

      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('❌ Kategori silinemedi:', error);
        return false;
      }

      console.log('✅ Kategori silindi:', id);
      await loadCategories();
      return true;
    } catch (error) {
      console.error('❌ Kategori silme hatası:', error);
      return false;
    }
  };

  const reloadCategories = async () => {
    await loadCategories();
  };

  const exportCategories = (): string => {
    return JSON.stringify(categories, null, 2);
  };

  const importCategories = async (jsonData: string): Promise<boolean> => {
    try {
      const parsed = JSON.parse(jsonData);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        return false;
      }

      const isValid = parsed.every(cat => 
        cat.id && cat.name && cat.slug && cat.icon
      );
      
      if (!isValid) {
        return false;
      }

      const dbCategories = parsed.map(cat => ({
        id: cat.id,
        name: cat.name,
        slug: cat.slug,
        icon: cat.icon,
        seo_title: cat.seoTitle || '',
        seo_description: cat.seoDescription || '',
        order: cat.order || 0,
        is_featured: cat.isFeatured || false,
      }));

      const { error } = await supabase
        .from('categories')
        .upsert(dbCategories, { onConflict: 'id' });

      if (error) {
        console.error('❌ Kategoriler içe aktarılamadı:', error);
        return false;
      }

      console.log('✅ Kategoriler içe aktarıldı');
      await loadCategories();
      return true;
    } catch (error) {
      console.error('❌ Kategori içe aktarma hatası:', error);
      return false;
    }
  };

  return (
    <CategoriesContext.Provider
      value={{
        categories,
        getCategoryById,
        addCategory,
        updateCategory,
        removeCategory,
        reloadCategories,
        exportCategories,
        importCategories,
        loading
      }}
    >
      {children}
    </CategoriesContext.Provider>
  );
};

export const useCategories = () => {
  const context = useContext(CategoriesContext);
  if (!context) {
    throw new Error("useCategories must be used within CategoriesProvider");
  }
  return context;
};
